// Simplified Chinese copy for the careers section.
// Job titles and skill tags stay in English (industry standard in CN tech),
// everything else is localized.

import type { EnrichedJob, RoleKey, RoleProfile } from "@/lib/jobs";
import type { ContentLang } from "@/lib/locale";

const BENEFITS_ZH = [
  "100% 远程办公, 波兰或中东欧地区任意地点",
  "B2B 合同或雇佣合同 - 由你选择",
  "直接对接招聘团队, 而不是没有温度的中介",
  "现代化工具链、带薪学习预算与技术大会日",
  "资深、务实的团队与极短的反馈循环",
];

const ROLE_PROFILES_ZH: Record<RoleKey, RoleProfile> = {
  "product-owner": {
    shortDescription:
      "端到端负责一个产品领域 - 从探索、路线图到交付 - 与工程、设计和业务方协作, 交付可衡量的成果。",
    responsibilities: [
      "为你负责的领域制定产品愿景与季度路线图",
      "把业务目标转化为优先级清晰、梳理完善的产品待办列表",
      "通过用户调研、数据分析和干系人访谈开展需求探索",
      "带领团队进行需求梳理、迭代规划与评审会议",
      "定义成功指标, 并在每次发布后复盘成果",
    ],
    requirements: [
      "3 年以上数字产品的 Product Owner 或 Product Manager 经验",
      "在敏捷团队中交付复杂功能的成功经历",
      "出色的干系人管理与沟通能力",
      "熟练使用待办管理工具 (Jira、Confluence) 与数据分析工具",
      "英语 B2 以上, 可与国际同事日常协作",
    ],
    niceToHave: [
      "零售、电商或全渠道平台经验",
      "掌握 SQL 或 BI 工具, 能自助完成数据分析",
      "服务设计或用户研究背景",
    ],
  },
  "product-designer": {
    shortDescription:
      "设计覆盖 Web 与移动端的端到端产品体验 - 从研究、信息架构到成熟设计系统中可直接交付的高保真 UI。",
    responsibilities: [
      "主导所负责产品领域的探索、创意与验证",
      "产出用户流程、线框图、原型与高保真界面",
      "参与并持续演进设计系统与共享组件",
      "开展可用性测试, 并把洞察转化为设计决策",
      "从概念到发布, 与产品经理和工程师紧密配合",
    ],
    requirements: [
      "具备已上线产品作品集, 最好是 B2C 或 SaaS 方向",
      "熟练掌握 Figma、原型设计与组件化思维",
      "扎实的交互设计与视觉设计功底",
      "能独立开展用户访谈并归纳结论",
      "英语 B2 以上, 可跨职能协作",
    ],
    niceToHave: [
      "使用过 Amplitude、Mixpanel 等分析工具",
      "动效设计能力或前端基础 (HTML / CSS)",
      "大规模设计系统的实践经验",
    ],
  },
  "data-analyst": {
    shortDescription:
      "把产品与业务数据转化为清晰、可执行的洞察 - 构建看板、深度分析与实验, 驱动团队决策。",
    responsibilities: [
      "定义 KPI 并为产品团队搭建自助式数据看板",
      "开展临时分析、A/B 实验解读与漏斗深度分析",
      "与产品经理共同设计实验并解释结果",
      "维护数据质量、指标口径与文档",
      "用通俗语言向业务方汇报分析结论",
    ],
    requirements: [
      "扎实的 SQL 能力, 能自如处理大型分析数据集",
      "熟练使用一种 BI 工具 (Looker、Tableau、Power BI 或同类)",
      "深入理解产品分析与实验方法",
      "具备 Python 或 R 的实操能力",
      "英语 B2 以上, 能撰写报告与演示",
    ],
    niceToHave: [
      "接触过 dbt、Snowflake、BigQuery 等数据仓库",
      "使用过 Amplitude、Mixpanel 或同类工具",
      "零售、金融科技或平台类分析背景",
    ],
  },
  "data-scientist": {
    shortDescription:
      "把业务问题转化为数据科学命题, 交付真正带来价值的模型 - 从探索性分析、原型到与平台团队一起上线。",
    responsibilities: [
      "与产品和业务方一起界定并排序机器学习机会",
      "构建、验证并上线预测型与决策型模型",
      "设计实验以衡量模型与功能的实际影响",
      "与工程团队协作完成生产部署与监控",
      "指导初级分析师, 提升团队分析水准",
    ],
    requirements: [
      "扎实的 Python (pandas、scikit-learn) 与 SQL 基础",
      "有把模型从 notebook 推向生产环境的实操经验",
      "良好的统计、实验设计与模型评估能力",
      "熟悉云数据平台 (AWS、GCP 或 Azure)",
      "英语 B2 以上, 能进行技术与干系人沟通",
    ],
    niceToHave: [
      "使用过 MLOps 工具 (MLflow、Kubeflow、SageMaker)",
      "深度学习框架经验 (PyTorch、TensorFlow)",
      "带领跨职能数据项目的经历",
    ],
  },
  "ml-engineer": {
    shortDescription:
      "让机器学习在规模化环境中落地 - 负责数据管道、模型服务与可观测性, 使数据科学成果稳定快速地触达用户。",
    responsibilities: [
      "设计并维护特征管道与训练工作流",
      "在批处理与实时链路中部署并监控模型",
      "为数据团队打造可复用的 ML 平台组件",
      "优化模型性能、延迟与基础设施成本",
      "与数据科学家从原型协作到发布",
    ],
    requirements: [
      "扎实的 Python 能力与软件工程最佳实践",
      "熟悉一种云平台 (AWS、GCP 或 Azure) 与 IaC 工具",
      "具备容器、CI/CD 与编排经验",
      "熟悉 ML 框架 (scikit-learn、PyTorch、TensorFlow)",
      "英语 B2 以上, 满足日常团队协作",
    ],
    niceToHave: [
      "流式与事件驱动架构 (Kafka、Pub/Sub)",
      "特征存储与 ML 可观测性经验",
      "接触过 GenAI / LLM 服务栈",
    ],
  },
  genai: {
    shortDescription:
      "构建生产级 GenAI 体验 - 从提示词设计、RAG 管道到评估与安全 - 并真正嵌入产品工作流。",
    responsibilities: [
      "端到端设计并交付基于 LLM 的功能",
      "搭建检索、提示与编排管道",
      "建立评估体系、guardrails 与可观测性",
      "对模型进行基准测试, 持续优化成本与质量的平衡",
      "指导产品团队采用负责任的 GenAI 实践",
    ],
    requirements: [
      "扎实的 Python 与软件工程基础",
      "熟练使用 LLM 工具链 (OpenAI、Azure OpenAI、LangChain 或同类)",
      "具备向量数据库与 RAG 架构经验",
      "深入理解提示工程与评估方法",
      "英语 B2 以上, 可进行技术协作",
    ],
    niceToHave: [
      "LLM 之外的 ML / NLP 背景",
      "微调或模型蒸馏经验",
      "熟悉 AI 在生产环境中的隐私与合规要求",
    ],
  },
  "systems-analyst": {
    shortDescription:
      "连接业务与工程 - 把模糊的需求转化为清晰的规格说明、流程图与验收标准, 让团队可以直接开发。",
    responsibilities: [
      "与干系人一起收集并梳理业务需求",
      "建模业务流程 (BPMN)、数据与系统集成 (UML)",
      "为工程团队编写用户故事与验收标准",
      "支持 QA 完成测试场景与 UAT",
      "在 Confluence 与 Jira 中维护持续更新的文档",
    ],
    requirements: [
      "具备 IT 项目中系统 / 业务分析师的实际经验",
      "熟练掌握 BPMN、UML 与需求工程方法",
      "具备 SQL 基础, 能进行数据探查",
      "较强的分析思维与清晰的书面表达",
      "英语 B2 以上, 可跨团队协作",
    ],
    niceToHave: [
      "零售 / POS / 收银生态系统经验",
      "熟悉 API 设计与集成模式",
      "接触过产品分析工具 (Amplitude、Mixpanel)",
    ],
  },
  "solutions-architect": {
    shortDescription:
      "掌控产品平台的技术蓝图 - 在架构、集成与非功能性需求上做出高杠杆决策。",
    responsibilities: [
      "为新老模块设计端到端架构",
      "定义跨服务的集成契约与数据流",
      "制定性能、安全与可扩展性等非功能标准",
      "指导工程师并评审关键设计决策",
      "与产品共同规划技术路线与取舍",
    ],
    requirements: [
      "5 年以上工程经验, 其中至少 2 年架构经验",
      "精通一种主流技术栈 (.NET / C#、Java 或同类)",
      "具备云架构与 CI/CD 流水线实操经验",
      "扎实的关系型数据库设计能力 (PostgreSQL 或同类)",
      "英语 B2 以上, 可撰写文档并跨团队协作",
    ],
    niceToHave: [
      "零售平台或全渠道商业系统经验",
      "大规模事件驱动与微服务架构经验",
      "云认证 (AWS、Azure 或 GCP)",
    ],
  },
  "data-architect": {
    shortDescription:
      "设计、演进并治理可扩展的云数据平台 - 包括数据仓库、数据湖、管道与数据治理 - 为全公司的分析、机器学习和产品决策提供数据能力支撑。",
    responsibilities: [
      "设计、演进并优化数据仓库架构, 使其可扩展、高性能并贴合业务需求",
      "在数据、文件存储和实时数据流领域定义、实施并执行架构标准与最佳实践",
      "制定一致的数据结构与命名规范, 提升方案的可读性与可维护性",
      "构建并配置数据架构师工具 (如数据目录), 并监督 ETL/ELT 的实施与演进",
      "确保用于业务分析的数据具备高质量、一致性和可用性",
      "保持数据架构一致性, 确保方案符合数据治理、安全与质量原则",
    ],
    requirements: [
      "3 年以上数据架构与云解决方案经验, 尤其是 GCP 或 Azure 环境",
      "具备 BigQuery 或 Snowflake 等云数据仓库实操经验, 精通 SQL",
      "熟悉数据管道设计、文档化与部署的最佳实践, 包括版本控制、CI/CD 与数据测试",
      "扎实理解数据建模、ETL/ELT 流程以及多源数据集成",
      "具备数据质量、信息安全与数据治理方面的知识",
    ],
    niceToHave: [
      "系统架构设计经验",
      "熟悉流式技术与实时数据处理架构",
    ],
    benefits: [
      "B2B 合同, 具有竞争力的费率",
      "波兹南与华沙办公室可选",
      "优惠福利: MultiSport、Luxmed 私立医疗、Uniqa 团体保险",
      "Maczfit、Samsung 门店及购车/租车折扣",
      "对数百万人日常使用的技术产生真实影响",
    ],
  },
  "senior-fullstack-supplychain": {
    shortDescription:
      "加入一家 YC W25 的 AI 原生供应链初创公司 (航空航天、电子、工业与国防) - 使用 TypeScript、React/Svelte 与 PostgreSQL 构建偏后端的全栈功能, 连接传统 ERP 与邮件驱动的工作流。20 人团队分布在华沙与旧金山, 融资超 1000 万美元, ARR 达七位数。全职, 华沙办公室现场办公。",
    responsibilities: [
      "构建并维护全栈功能, 从数据库结构到用户界面",
      "将传统 ERP 系统与客户日常使用的工作流集成",
      "使用 TypeScript 构建后端服务, 集成前端框架与 PostgreSQL",
      "打通处理真实客户订单的邮件驱动流程",
      "并行管理多个项目并推动每个项目交付",
      "与平台团队及前线部署工程师紧密协作",
    ],
    requirements: [
      "理想情况下 5 年以上全栈经验",
      "具备独立系统设计经验与扎实的 TypeScript 能力",
      "精通 React、Svelte 或 Next.js 等前端框架",
      "能够编写、优化并调试 ORM 之外的复杂 SQL 查询",
      "日常使用 AI 编程工具并了解其最新能力",
      "英语流利, 能与二十出头的创始人顺畅合作",
      "工作勤奋、适应力强、低自我; 好奇、诚实、结果导向",
      "华沙现场办公, 每季度最多一周国际出差",
    ],
    niceToHave: [
      "国际化部署 SaaS 产品经验",
      "A-C 轮初创公司经验",
    ],
    benefits: [
      "B2B 合同, 具有竞争力的费率",
      "可与团队一起入住华沙 hackerhouse",
      "年轻的团队, 老板们二十出头",
      "Fit-to-Win Sprint 流程: 反馈迅速, 决策明确",
    ],
  },
  "senior-llm-python": {
    shortDescription:
      "以资深工程师身份构建基于 LLM 的 Python 服务 - 从架构设计、数据管道到生产上线与可观测性。",
    responsibilities: [
      "设计并实现基于 LLM 的 Python 后端服务",
      "构建检索、编排与评估管道",
      "确保代码质量、测试覆盖与生产可靠性",
      "优化推理成本、延迟与结果质量",
      "参与代码评审与关键技术决策",
    ],
    requirements: [
      "5 年以上 Python 工程经验",
      "具备 LLM / GenAI 生产项目经验",
      "熟悉云平台、容器与 CI/CD",
      "扎实的 API 设计与测试实践",
      "英语 B2 以上, 满足日常协作",
    ],
    niceToHave: [
      "向量数据库与 RAG 架构经验",
      "MLOps 与模型可观测性经验",
      "开源项目贡献经历",
    ],
  },
  "senior-java-fintech": {
    shortDescription:
      "为一家已实现盈利、并获风险投资支持的 API-first 金融科技平台构建后端 - 覆盖虚拟 IBAN、发卡、境内与跨境支付、核心银行以及 AML/KYC。",
    responsibilities: [
      "设计并实现后端服务 (Java、Spring Boot、Kafka、PostgreSQL)",
      "参与系统架构设计与关键技术决策",
      "构建可扩展、可靠、易维护的分布式系统",
      "使用云基础设施 (AWS、Docker、CI/CD、Terraform)",
      "端到端负责功能与系统",
      "与管理层及其他工程师直接协作, 没有中间层",
    ],
    requirements: [
      "5 年以上 Java 软件工程师经验",
      "能够独立交付完整的后端解决方案",
      "精通 Java、Spring Boot 与 PostgreSQL",
      "具备 Kafka 或同类事件流平台经验",
      "熟悉 AWS 与现代 DevOps 实践",
      "高工程标准、强主人翁意识与良好的自我管理能力",
      "英语 B2 以上, 可与英国团队日常协作",
    ],
    niceToHave: [
      "金融科技或支付领域背景 (非必需)",
      "可参与前端开发 (React、TypeScript)",
      "DevOps 流程与基础设施自动化经验",
    ],
    benefits: [
      "产品公司, 已有真实客户并实现盈利, 获风险投资支持",
      "API-first 平台: 支付、核心银行、发卡、AML/KYC",
      "与决策者直接沟通, 没有中间层",
      "100% 波兰远程办公",
      "Fit-to-Win Sprint 流程: 反馈迅速, 决策明确",
    ],
  },
  "senior-qa-playwright": {
    shortDescription:
      "端到端负责软件质量 - 设计并执行测试策略, 使用 Playwright 构建回归与冒烟测试套件, 并与工程团队一起完成缺陷分析、CI/CD 集成与发布准备。",
    responsibilities: [
      "使用 Playwright 设计、实现并维护自动化测试",
      "为 Web、API 与集成场景制定测试策略、计划与覆盖范围",
      "构建并维护回归与冒烟测试套件",
      "验证 REST API (功能、集成与非功能测试)",
      "记录测试指标, 并在 Jira/Confluence 中保证可追溯性",
      "支持 CI/CD 集成、测试稳定性与发布准备",
      "与开发协作完成缺陷分析、根因定位与可测性改进",
      "与产品一起明确需求、验收标准与用户画像",
    ],
    requirements: [
      "5 年以上 QA 工程经验",
      "具备 Playwright UI 自动化测试的实际经验",
      "深入理解 REST API、OpenAPI/Swagger 与接口测试",
      "掌握基础 Web 技术 (HTML/CSS/JS、浏览器开发者工具)",
      "熟悉 CI/CD、Docker 与敏捷环境 (Scrum/Kanban)",
      "掌握 TypeScript 与 Python，用于测试自动化与接口示例",
      "计算机或相关专业本科及以上学历",
      "优秀的英语沟通能力 (口语与书面)",
    ],
    niceToHave: [
      "性能或安全测试经验",
      "契约测试或测试数据管理经验",
      "移动端测试背景",
    ],
    benefits: [
      "B2B 合同, 薪资依据资历确定",
      "完整福利package (Luxmed 私立医疗、Multisport 等)",
      "每年 26 天带薪年假",
      "每年 14 天带薪病假",
      "波兰境内 100% 远程办公",
      "现代化工具链、学习预算与资深务实的团队",
    ],
  },
  qa: {
    shortDescription:
      "全面提升产品质量标准 - 设计测试策略、构建自动化覆盖, 并与工程团队协作实现有把握的发布。",
    responsibilities: [
      "设计并维护自动化测试套件 (UI、API、E2E)",
      "为新功能制定测试策略与覆盖范围",
      "对关键用户旅程开展探索性测试",
      "把测试接入 CI/CD，并快速定位失败原因",
      "在团队中推动质量实践",
    ],
    requirements: [
      "至少熟练使用一种自动化测试框架",
      "扎实的编程基础 (TypeScript、Java 或 Python)",
      "掌握 REST API 与 SQL",
      "具备 CI/CD 工具与版本管理经验",
      "英语 B2 以上, 可进行技术沟通",
    ],
    niceToHave: [
      "性能或安全测试经验",
      "契约测试或测试数据管理经验",
      "移动端测试背景",
    ],
  },
  java: {
    shortDescription:
      "使用现代 Java 构建高可靠后端服务 - 设计清晰的 API，对线上服务负责, 并持续改善平台的长期健康度。",
    responsibilities: [
      "设计、开发并运维 Java 微服务",
      "定义清晰的 API 并对接内外部系统",
      "编写单元、集成与契约层级的测试",
      "针对生产负载进行性能剖析、调优与加固",
      "参与代码评审与技术决策",
    ],
    requirements: [
      "4 年以上现代 Java (17+) 与 Spring Boot 经验",
      "扎实的关系型数据库经验 (PostgreSQL、MySQL)",
      "具备云平台 (AWS / Azure / GCP) 与 CI/CD 实操经验",
      "熟悉测试金字塔与整洁代码实践",
      "英语 B2 以上, 满足日常协作",
    ],
    niceToHave: [
      "事件驱动架构经验 (Kafka、RabbitMQ)",
      "容器、Kubernetes 与基础设施即代码",
      "零售、金融科技或高吞吐系统背景",
    ],
  },
  fallback: {
    shortDescription:
      "加入一支资深、远程优先的团队, 交付现代数字产品 - 高度自主、扎实的工程文化, 以及对产品走向的直接影响力。",
    responsibilities: [
      "端到端负责产品中有分量的模块",
      "与产品、设计和工程同事紧密协作",
      "参与技术与流程决策",
      "帮助团队持续提升质量标准",
      "通过评审、文档与结对分享知识",
    ],
    requirements: [
      "具备同类岗位在生产级系统上的实际经验",
      "扎实的专业基本功与最佳实践意识",
      "适合远程团队的自驱工作方式",
      "清晰的书面与口头表达能力",
      "英语 B2 以上, 满足日常协作",
    ],
    niceToHave: [
      "分布式、跨文化团队的工作经验",
      "接触过现代敏捷实践与持续交付",
      "带教或技术负责人经历",
    ],
  },
};

const CATEGORY_ZH: Record<string, string> = {
  All: "全部",
  Data: "数据",
  Analytics: "数据分析",
  "AI / ML": "AI / ML",
  Product: "产品",
  Design: "设计",
  QA: "测试",
  Java: "Java",
  Architecture: "架构",
  DevOps: "DevOps",
  Programming: "编程开发",
  PROGRAMMING: "编程开发",
  Backend: "后端开发",
  Frontend: "前端开发",
  Fullstack: "全栈开发",
  Mobile: "移动开发",
  Security: "安全",
  PM: "项目管理",
  Other: "其他",
};

const WORKMODE_ZH: Record<string, string> = {
  Remote: "远程",
  REMOTE: "远程",
  Hybrid: "混合办公",
  Office: "驻场办公",
};

const SALARY_ZH: Record<string, string> = {
  "Undisclosed Salary": "薪资面议",
};

/** Localized category label (the raw value stays the filter key). */
export const categoryLabel = (cat: string, lang: ContentLang) =>
  lang === "zh" ? CATEGORY_ZH[cat] ?? cat : cat;

/** Translate the editorial parts of a job card into the active language. */
export function localizeJob(job: EnrichedJob, lang: ContentLang): EnrichedJob {
  if (lang !== "zh") return job;
  const p = ROLE_PROFILES_ZH[job.role] ?? ROLE_PROFILES_ZH.fallback;
  return {
    ...job,
    location: WORKMODE_ZH[job.location] ?? job.location,
    workmode: WORKMODE_ZH[job.workmode] ?? job.workmode,
    salary: SALARY_ZH[job.salary] ?? job.salary,
    shortDescription: p.shortDescription,
    responsibilities: p.responsibilities,
    requirements: p.requirements,
    niceToHave: p.niceToHave,
    benefits: p.benefits ?? BENEFITS_ZH,
  };
}
